import { useEffect, useMemo, useState } from 'react';
import { GoogleMap, OverlayView, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { keyframes } from '@emotion/react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, Tour } from '@mui/icons-material';
import useFirebase from 'hooks/useFirebase';
import usePresence from 'hooks/usePresence';

const containerStyle = {
  width: '100%',
  height: '300px'
};

const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 0.8; }
  50%  { transform: scale(1.8);  opacity: 0.3; }
  100% { transform: scale(2.5);  opacity: 0; }
`;

// Stable palette cycled by index. Picked to stay distinguishable on a Google Maps
// road background and to keep a recognisable green/red split between start and end.
const USER_COLORS = ['#1976d2', '#9c27b0', '#0097a7', '#f57c00', '#388e3c', '#c2185b'];

export interface ParticipantInfo {
  uid: string;
  name?: string;
}

// Pickup / destination address persisted on the transaction (authkeys.model.ts).
// `coordinates` is [lat, lng] and may be absent when backend geocoding was
// unavailable, in which case the address string is geocoded client-side.
export interface AddressLocation {
  address?: string | null;
  coordinates?: number[] | null;
}

interface ResolvedAddressPoint {
  label: 'Pickup' | 'Destination';
  color: string;
  address: string;
  pos: google.maps.LatLngLiteral;
}

const PICKUP_COLOR = '#2e7d32';
const DESTINATION_COLOR = '#d32f2f';

const addrHasCoords = (loc?: AddressLocation | null): loc is AddressLocation & { coordinates: number[] } =>
  Array.isArray(loc?.coordinates) &&
  loc!.coordinates!.length >= 2 &&
  !isNaN(Number(loc!.coordinates![0])) &&
  !isNaN(Number(loc!.coordinates![1]));

const addrHasAddress = (loc?: AddressLocation | null): boolean => Boolean((loc?.address || '').trim());

/**
 * The map renders driver tracking only — routes, start/end/live markers.
 *
 * Monitoring alerts used to be drawn here as one blinking pin per alert, which
 * buried the route on any busy transaction. Worse, alerts with no coordinates of
 * their own (STEP_TIMER / INTER_STEP_TRACKING — a step being overdue is not a
 * place) were fanned out in a ring around the driver's last position, asserting
 * that something happened at spots where nothing did. Alerts now live only in the
 * grouped cards of the Info and Alerts panel.
 */
interface LiveTrackingMapProps {
  txnId: string;
  participants?: ParticipantInfo[];
  pickup?: AddressLocation | null;
  destination?: AddressLocation | null;
}

interface UserTrack {
  uid: string;
  name: string;
  color: string;
  status: 'active' | 'completed' | 'unknown';
  start: google.maps.LatLngLiteral | null;
  end: google.maps.LatLngLiteral | null;
  path: google.maps.LatLngLiteral[];
  /** Epoch ms for each point in `path`, same index order (undefined when a point
   * carried no timestamp). Drives the dashed gap segments. */
  pathTimestamps: (number | undefined)[];
  startTimestamp?: number;
  endTimestamp?: number;
}

/** A gap longer than this between two consecutive track points is drawn dashed
 * rather than a solid line: we have no positions for that stretch, so a solid
 * line would imply a route we never saw. Points push ~once a minute, so 5 min is
 * about five missed points. Kept in step with the app's _gapThresholdMs
 * (quik-connect-v2 map_tab.dart). */
const TRACKING_GAP_MS = 5 * 60 * 1000;

/** History timestamps come as a number (weblink writes Date.now()) OR an ISO
 * string (native app writes DateTime.now().toIso8601String()). Normalise both to
 * epoch ms — reading only numbers silently dropped every app-written point and
 * left gaps undetected. */
const parseHistoryTs = (v: unknown): number | undefined => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

/** Dashed style for a gap segment: Google Maps has no dash-array, so a repeated
 * short line symbol is drawn on an otherwise invisible stroke (the documented
 * approach). Defined as a plain literal so it never touches `google.*` at load. */
const GAP_DASH: google.maps.Symbol = {
  path: 'M 0,-1 0,1',
  strokeOpacity: 0.7,
  strokeWeight: 4,
  scale: 3
};

interface TrackSegment {
  points: google.maps.LatLngLiteral[];
  dashed: boolean;
}

/** Split an ordered path into solid runs and dashed gap edges. A pair of points
 * more than TRACKING_GAP_MS apart has no data between them, so that single edge
 * is dashed; everything else stays a continuous solid line. */
const buildTrackSegments = (
  points: google.maps.LatLngLiteral[],
  times: (number | undefined)[]
): TrackSegment[] => {
  if (points.length < 2) return [];
  const segs: TrackSegment[] = [];
  let run: google.maps.LatLngLiteral[] = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const ta = times[i];
    const tb = times[i + 1];
    const isGap = typeof ta === 'number' && typeof tb === 'number' && tb - ta > TRACKING_GAP_MS;
    if (isGap) {
      if (run.length >= 2) segs.push({ points: run, dashed: false });
      segs.push({ points: [points[i], points[i + 1]], dashed: true });
      run = [points[i + 1]];
    } else {
      run.push(points[i + 1]);
    }
  }
  if (run.length >= 2) segs.push({ points: run, dashed: false });
  return segs;
};

const shortenUid = (uid: string) => (uid.length > 8 ? `${uid.slice(0, 6)}…` : uid);

const toRad = (deg: number) => (deg * Math.PI) / 180;
const distMeters = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) => {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const TILE_SIZE = 256;

const projectToWorld = (latLng: google.maps.LatLngLiteral) => {
  let siny = Math.sin((latLng.lat * Math.PI) / 180);
  siny = Math.min(Math.max(siny, -0.9999), 0.9999);
  return {
    x: TILE_SIZE * (0.5 + latLng.lng / 360),
    y: TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
  };
};

const unprojectFromWorld = (point: { x: number; y: number }): google.maps.LatLngLiteral => {
  const lng = (point.x / TILE_SIZE - 0.5) * 360;
  const n = Math.PI - (2 * Math.PI * point.y) / TILE_SIZE;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
};

const toPixel = (latLng: google.maps.LatLngLiteral, zoom: number) => {
  const scale = Math.pow(2, zoom);
  const world = projectToWorld(latLng);
  return { x: world.x * scale, y: world.y * scale };
};

const fromPixel = (point: { x: number; y: number }, zoom: number): google.maps.LatLngLiteral => {
  const scale = Math.pow(2, zoom);
  return unprojectFromWorld({ x: point.x / scale, y: point.y / scale });
};

const pixelBearing = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.atan2(b.x - a.x, -(b.y - a.y));

const offsetPathPixels = (path: google.maps.LatLngLiteral[], zoom: number, pixelOffset: number): google.maps.LatLngLiteral[] => {
  if (pixelOffset === 0 || path.length < 2) return path;
  const pixels = path.map((p) => toPixel(p, zoom));
  return pixels.map((pt, i) => {
    const segStart = i < pixels.length - 1 ? pt : pixels[i - 1];
    const segEnd = i < pixels.length - 1 ? pixels[i + 1] : pt;
    const bearing = pixelBearing(segStart, segEnd) + Math.PI / 2; // perpendicular, in radians
    const shifted = {
      x: pt.x + Math.sin(bearing) * pixelOffset,
      y: pt.y - Math.cos(bearing) * pixelOffset
    };
    return fromPixel(shifted, zoom);
  });
};

const offsetSinglePoint = (
  point: google.maps.LatLngLiteral,
  refPoint: google.maps.LatLngLiteral,
  zoom: number,
  pixelOffset: number
): google.maps.LatLngLiteral => {
  if (pixelOffset === 0) return point;
  const p1 = toPixel(point, zoom);
  const p2 = toPixel(refPoint, zoom);

  const bearing = p1.x === p2.x && p1.y === p2.y ? 0 : pixelBearing(p2, p1) + Math.PI / 2;
  const shifted = {
    x: p1.x + Math.sin(bearing) * pixelOffset,
    y: p1.y - Math.cos(bearing) * pixelOffset
  };
  return fromPixel(shifted, zoom);
};

const spreadCoincidentPositions = (
  points: { key: string; pos: google.maps.LatLngLiteral }[],
  zoom: number,
  pixelThreshold = 25,
  pixelSpacing = 40
): Map<string, google.maps.LatLngLiteral> => {
  const pixelPoints = points.map((p) => ({ key: p.key, pixel: toPixel(p.pos, zoom) }));
  const groups: (typeof pixelPoints)[] = [];
  pixelPoints.forEach((p) => {
    const group = groups.find((g) => g.some((m) => Math.hypot(m.pixel.x - p.pixel.x, m.pixel.y - p.pixel.y) < pixelThreshold));
    if (group) group.push(p);
    else groups.push([p]);
  });

  const result = new Map<string, google.maps.LatLngLiteral>();
  groups.forEach((group) => {
    if (group.length === 1) {
      const only = group[0];
      result.set(only.key, points.find((p) => p.key === only.key)!.pos);
      return;
    }
    const cx = group.reduce((s, m) => s + m.pixel.x, 0) / group.length;
    const cy = group.reduce((s, m) => s + m.pixel.y, 0) / group.length;
    const startX = cx - (pixelSpacing * (group.length - 1)) / 2;
    group.forEach((m, idx) => {
      const px = startX + idx * pixelSpacing;
      result.set(m.key, fromPixel({ x: px, y: cy }, zoom));
    });
  });
  return result;
};

const estimateLabelWidthPx = (text: string) => text.length * 6.5 + 24;

const computeLabelTiers = (markers: { key: string; pixelX: number; widthPx: number }[], buffer = 8): Map<string, number> => {
  const sorted = [...markers].sort((a, b) => a.pixelX - b.pixelX);
  const tierIntervals: { start: number; end: number }[][] = [];
  const result = new Map<string, number>();

  sorted.forEach((m) => {
    const start = m.pixelX - m.widthPx / 2;
    const end = m.pixelX + m.widthPx / 2;
    let tier = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const intervals = tierIntervals[tier] || [];
      const collides = intervals.some((iv) => start < iv.end + buffer && end > iv.start - buffer);
      if (!collides) {
        if (!tierIntervals[tier]) tierIntervals[tier] = [];
        tierIntervals[tier].push({ start, end });
        result.set(m.key, tier);
        break;
      }
      tier += 1;
    }
  });

  return result;
};

/** Grey used for a driver whose device has gone dark, so a stale position never
 *  reads as a live one. Matches the app's offline pin (0xFF9AA1AE). */
const OFFLINE_COLOR = '#9aa1ae';

const TrackMarker = ({
  position,
  color,
  label,
  badge,
  isActive,
  labelOffsetPx = 32
}: {
  position: google.maps.LatLngLiteral;
  color: string;
  label: string;
  badge?: 'START' | 'END' | 'NOW' | 'STOPPED' | 'START / END' | 'OFFLINE' | 'PICKUP' | 'DESTINATION';
  isActive?: boolean;
  labelOffsetPx?: number;
}) => (
  <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
    <Box sx={{ position: 'relative', transform: 'translate(-50%, -100%)' }}>
      {isActive && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: `${color}66`,
            animation: `${pulse} 1.5s ease-out infinite`
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: -labelOffsetPx,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: color,
          color: '#fff',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '10px',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          transition: 'top 0.15s ease-out',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -5,
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: `5px solid ${color}`
          }
        }}
      >
        {badge ? `${label} · ${badge}` : label}
      </Box>
      <Box
        sx={{
          width: 30,
          height: 30,
          background: color,
          borderRadius: '50% 50% 50% 0',
          transform: 'rotate(-45deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid #fff',
          filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.35))'
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            backgroundColor: '#fff',
            borderRadius: '50%',
            transform: 'rotate(45deg)'
          }}
        />
      </Box>
    </Box>
  </OverlayView>
);

// Pickup / destination marker — a rounded square tag with an icon, deliberately a
// different silhouette from the driver teardrop (TrackMarker) so a location pin is
// never mistaken for a driver.
const AddressMarker = ({
  position,
  color,
  label
}: {
  position: google.maps.LatLngLiteral;
  color: string;
  label: string;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <Box
        sx={{ position: 'relative', transform: 'translate(-50%, -100%)' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: color,
              color: '#fff',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '10px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              pointerEvents: 'none',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -5,
                left: '50%',
                transform: 'translateX(-50%)',
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: `5px solid ${color}`
              }
            }}
          >
            {label}
          </Box>
        )}
        <Tour
          sx={{
            color,
            fontSize: 40,
            display: 'block',
            cursor: 'pointer',
            '& path': {
              stroke: '#fff',
              strokeWidth: 1.4,
              strokeLinejoin: 'round',
              paintOrder: 'stroke'
            },
            filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.45))'
          }}
        />
      </Box>
    </OverlayView>
  );
};

const LiveTrackingMap = ({ txnId, participants = [], pickup, destination }: LiveTrackingMapProps) => {
  const { subscribeToTransactionLocations } = useFirebase();
  const [tracks, setTracks] = useState<UserTrack[]>([]);
  const [loading, setLoading] = useState(true);

  // Pickup / destination pins shown on the same tracking map. `addrError` is
  // true when one or both addresses were provided but could not be located,
  // so the section can say so instead of silently dropping them.
  const [addrPoints, setAddrPoints] = useState<ResolvedAddressPoint[]>([]);
  const [addrError, setAddrError] = useState(false);

  const LANE_OFFSET_PIXELS = 5;

  const [zoom, setZoom] = useState(17);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const handleZoomChanged = () => {
    if (mapInstance) {
      const z = mapInstance.getZoom();
      if (typeof z === 'number') setZoom(z);
    }
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  });

  // Resolve pickup / destination to map coordinates: use stored coords when
  // present, else geocode the address string. Re-runs only when the actual
  // address/coords change.
  const pickupKey = `${pickup?.address || ''}|${(pickup?.coordinates || []).join(',')}`;
  const destinationKey = `${destination?.address || ''}|${(destination?.coordinates || []).join(',')}`;

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;

    const inputs = (
      [
        { label: 'Pickup' as const, color: PICKUP_COLOR, loc: pickup },
        { label: 'Destination' as const, color: DESTINATION_COLOR, loc: destination }
      ]
    ).filter(({ loc }) => addrHasCoords(loc) || addrHasAddress(loc));

    if (inputs.length === 0) {
      setAddrPoints([]);
      setAddrError(false);
      return;
    }

    (async () => {
      const geocoder = new google.maps.Geocoder();
      const results = await Promise.all(
        inputs.map(async ({ label, color, loc }): Promise<ResolvedAddressPoint | null> => {
          if (addrHasCoords(loc)) {
            return {
              label,
              color,
              address: loc!.address || '',
              pos: { lat: Number(loc!.coordinates![0]), lng: Number(loc!.coordinates![1]) }
            };
          }
          try {
            const res = await geocoder.geocode({ address: loc!.address as string });
            const g = res.results?.[0]?.geometry?.location;
            if (g) return { label, color, address: loc!.address as string, pos: { lat: g.lat(), lng: g.lng() } };
          } catch {
            // Unresolvable — counted as a failure below.
          }
          return null;
        })
      );
      if (cancelled) return;
      const ok = results.filter(Boolean) as ResolvedAddressPoint[];
      setAddrPoints(ok);
      // Some address was requested but could not be placed on the map.
      setAddrError(ok.length < inputs.length);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, pickupKey, destinationKey]);

  // Live device presence for everyone plotted. Distinct from `status`, which is
  // the state of the TRACKING SESSION: a driver whose phone died keeps
  // status 'active' and a last-known position forever, and used to render here
  // as a pulsing live marker indefinitely. Keyed off the uids actually on the
  // map rather than the participants prop, since the tracking node is the
  // authority on who is being tracked.
  const trackedUids = useMemo(() => tracks.map((t) => t.uid), [tracks]);

  // True when any route has a reporting gap, so the "No tracking data" legend is
  // only shown when there's actually a dashed segment on the map to explain.
  const hasTrackingGap = useMemo(
    () =>
      tracks.some((t) => {
        for (let k = 1; k < t.pathTimestamps.length; k++) {
          const a = t.pathTimestamps[k - 1];
          const b = t.pathTimestamps[k];
          if (typeof a === 'number' && typeof b === 'number' && b - a > TRACKING_GAP_MS) return true;
        }
        return false;
      }),
    [tracks]
  );
  const presence = usePresence(trackedUids);

  // Resolve a display name from the participants prop. Falls back to a short uid.
  const nameFor = useMemo(() => {
    const byUid = new Map<string, string>();
    participants.forEach((p) => {
      if (p.uid && p.name) byUid.set(p.uid, p.name);
    });
    return (uid: string) => byUid.get(uid) || shortenUid(uid);
  }, [participants]);

  useEffect(() => {
    if (!txnId) return;
    const unsubscribe = subscribeToTransactionLocations(txnId, (data) => {
      if (!data) {
        setTracks([]);
        setLoading(false);
        return;
      }

      const entries = Object.entries(data) as [string, any][];
      const built: UserTrack[] = entries.map(([uid, raw], idx) => {
        const color = USER_COLORS[idx % USER_COLORS.length];
        const start =
          raw?.startLocation && typeof raw.startLocation.lat === 'number'
            ? { lat: raw.startLocation.lat, lng: raw.startLocation.lng }
            : null;
        const end =
          raw?.endLocation && typeof raw.endLocation.lat === 'number' ? { lat: raw.endLocation.lat, lng: raw.endLocation.lng } : null;
        // History is append-only: native writes each point as a child under
        // `history` (pushId → point), so it arrives as an object, not an array.
        // Push keys sort chronologically. Stay backward-compatible with the
        // legacy array shape.
        const rawHistory = raw?.history;
        const history: any[] = Array.isArray(rawHistory)
          ? rawHistory
          : rawHistory && typeof rawHistory === 'object'
          ? Object.keys(rawHistory)
              .sort()
              .map((k) => rawHistory[k])
          : [];
        const pts = history.filter((h) => typeof h?.lat === 'number' && typeof h?.lng === 'number');
        const path: google.maps.LatLngLiteral[] = pts.map((h) => ({ lat: h.lat, lng: h.lng }));
        const pathTimestamps: (number | undefined)[] = pts.map((h) => parseHistoryTs(h?.timestamp));
        const status: UserTrack['status'] = raw?.status === 'active' || raw?.status === 'completed' ? raw.status : 'unknown';
        return {
          uid,
          name: nameFor(uid),
          color,
          status,
          start,
          end,
          path,
          pathTimestamps,
          startTimestamp: typeof raw?.startLocation?.timestamp === 'number' ? raw.startLocation.timestamp : undefined,
          endTimestamp: typeof raw?.endLocation?.timestamp === 'number' ? raw.endLocation.timestamp : undefined
        };
      });

      // Sort so completed tracks render under active ones (active overlays win).
      built.sort((a, b) => (a.status === 'active' ? 1 : 0) - (b.status === 'active' ? 1 : 0));
      setTracks(built);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [txnId, subscribeToTransactionLocations, nameFor]);

  // Center: prefer the most recent active position; otherwise the first available
  // start point. Bounds-fit done after isLoaded via onLoad.
  const center = useMemo<google.maps.LatLngLiteral>(() => {
    for (const t of tracks) {
      if (t.status === 'active') {
        const last = t.path[t.path.length - 1];
        if (last) return last;
        if (t.start) return t.start;
      }
    }
    for (const t of tracks) {
      if (t.start) return t.start;
      if (t.end) return t.end;
      // Last-known GPS point — covers a completed process-level track (no
      // start/end, e.g. an admin-stopped driver) so the map centres on the
      // STOPPED marker instead of falling through to {0,0} and pushing it
      // off-screen.
      const last = t.path[t.path.length - 1];
      if (last) return last;
    }
    // No driver positions — centre on a pickup/destination pin if we have one.
    if (addrPoints[0]) return addrPoints[0].pos;
    return { lat: 0, lng: 0 };
  }, [tracks, addrPoints]);

  const trackRenderData = useMemo(() => {
    return tracks.map((t, i) => {
      const laneOffsetPx = tracks.length > 1 ? (i - (tracks.length - 1) / 2) * LANE_OFFSET_PIXELS : 0;

      const hasLine = t.path.length >= 2;
      const offsetTrackPath = hasLine ? offsetPathPixels(t.path, zoom, laneOffsetPx) : t.path;

      const alignedStart = t.start
        ? hasLine
          ? offsetTrackPath[0]
          : offsetSinglePoint(t.start, t.end ?? t.start, zoom, laneOffsetPx)
        : null;

      const alignedEnd = t.end
        ? hasLine
          ? offsetTrackPath[offsetTrackPath.length - 1]
          : offsetSinglePoint(t.end, t.start ?? t.end, zoom, laneOffsetPx)
        : null;

      // For active tracks without endLocation, this is the live "NOW" position.
      // For completed tracks without endLocation (an admin stopped tracking
      // without ever writing an endLocation), this is the "STOPPED" position.
      // Either way we need the last-known GPS point so the driver never
      // vanishes from the map.
      const rawLastKnownPosition =
        !t.end && (t.status === 'active' || t.status === 'completed') ? t.path[t.path.length - 1] || t.start : null;
      const alignedLastKnown = rawLastKnownPosition
        ? hasLine
          ? offsetTrackPath[offsetTrackPath.length - 1]
          : offsetSinglePoint(rawLastKnownPosition, t.start ?? rawLastKnownPosition, zoom, laneOffsetPx)
        : null;

      return { uid: t.uid, offsetTrackPath, alignedStart, alignedEnd, alignedLastKnown };
    });
  }, [tracks, zoom]);

  const adjustedMarkerPositions = useMemo(() => {
    const points: { key: string; pos: google.maps.LatLngLiteral }[] = [];
    trackRenderData.forEach((r) => {
      if (r.alignedStart) points.push({ key: `${r.uid}-start`, pos: r.alignedStart });
      if (r.alignedEnd) points.push({ key: `${r.uid}-end`, pos: r.alignedEnd });
      if (r.alignedLastKnown) points.push({ key: `${r.uid}-now`, pos: r.alignedLastKnown });
    });
    return spreadCoincidentPositions(points, zoom);
  }, [trackRenderData, zoom]);

  const visibleMarkers = useMemo(() => {
    const list: {
      key: string;
      uid: string;
      color: string;
      name: string;
      badge: 'START' | 'END' | 'NOW' | 'STOPPED' | 'START / END' | 'OFFLINE';
      pos: google.maps.LatLngLiteral;
      isActive?: boolean;
    }[] = [];

    const COINCIDENT_THRESHOLD = 0.000001;

    tracks.forEach((t, i) => {
      const r = trackRenderData[i];
      const coincident =
        t.start &&
        t.end &&
        Math.abs(t.start.lat - t.end.lat) < COINCIDENT_THRESHOLD &&
        Math.abs(t.start.lng - t.end.lng) < COINCIDENT_THRESHOLD;

      if (coincident && r.alignedStart) {
        list.push({
          key: `${t.uid}-start`,
          uid: t.uid,
          color: t.color,
          name: t.name,
          badge: 'START / END',
          pos: adjustedMarkerPositions.get(`${t.uid}-start`) || r.alignedStart
        });
      } else {
        if (r.alignedStart) {
          list.push({
            key: `${t.uid}-start`,
            uid: t.uid,
            color: t.color,
            name: t.name,
            badge: 'START',
            pos: adjustedMarkerPositions.get(`${t.uid}-start`) || r.alignedStart
          });
        }
        if (r.alignedEnd) {
          list.push({
            key: `${t.uid}-end`,
            uid: t.uid,
            color: t.color,
            name: t.name,
            badge: 'END',
            pos: adjustedMarkerPositions.get(`${t.uid}-end`) || r.alignedEnd
          });
        }
      }

      if (r.alignedLastKnown) {
        // A tracking session that is still 'active' but whose device stopped
        // reporting is neither NOW nor STOPPED — the position is simply stale.
        // Greyed and badged OFFLINE, and the pulse is dropped, so it cannot be
        // mistaken for a driver who is actually there right now.
        const offline = t.status === 'active' && !presence[t.uid]?.online;
        list.push({
          key: `${t.uid}-now`,
          uid: t.uid,
          color: offline ? OFFLINE_COLOR : t.color,
          name: t.name,
          badge: t.status === 'active' ? (offline ? 'OFFLINE' : 'NOW') : 'STOPPED',
          pos: adjustedMarkerPositions.get(`${t.uid}-now`) || r.alignedLastKnown,
          isActive: t.status === 'active' && !offline
        });
      }
    });

    return list;
  }, [tracks, trackRenderData, adjustedMarkerPositions, presence]);

  const labelTiers = useMemo(() => {
    const withPixel = visibleMarkers.map((m) => {
      const px = toPixel(m.pos, zoom);
      const text = `${m.name} · ${m.badge}`;
      return { key: m.key, pixelX: px.x, widthPx: estimateLabelWidthPx(text) };
    });
    return computeLabelTiers(withPixel);
  }, [visibleMarkers, zoom]);

  const fitAllPoints = (map: google.maps.Map) => {
    const all: google.maps.LatLngLiteral[] = [];
    tracks.forEach((t) => {
      if (t.start) all.push(t.start);
      if (t.end) all.push(t.end);
      all.push(...t.path);
    });
    // Frame the pickup/destination pins alongside the driver route.
    addrPoints.forEach((a) => all.push(a.pos));
    if (all.length <= 1) {
      const z = map.getZoom();
      if (typeof z === 'number') setZoom(z);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    all.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 40);
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const z = map.getZoom();
      if (typeof z === 'number' && z > 17) map.setZoom(17);
      // Sync zoom state after fitBounds settles, so pixel-space offsets are
      // computed against the zoom level actually shown to the user.
      const finalZoom = map.getZoom();
      if (typeof finalZoom === 'number') setZoom(finalZoom);
    });
  };

  const onMapLoad = (map: google.maps.Map) => {
    setMapInstance(map);
    fitAllPoints(map);
  };

  // Re-frame once pickup/destination pins resolve after the map has loaded
  // (geocoding is async, so they usually arrive after onMapLoad).
  useEffect(() => {
    if (mapInstance && addrPoints.length > 0) {
      fitAllPoints(mapInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, addrPoints]);

  if (loading || !isLoaded) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading live tracking map…
        </Typography>
      </Box>
    );
  }

  if (tracks.length === 0 && addrPoints.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {addrError
            ? 'Unable to display pickup / destination address on the map.'
            : 'No live tracking data yet for this transaction.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {addrError && (
        <Box sx={{ mx: 1, mt: 1 }}>
          <Typography variant="caption" color="error">
            Unable to display pickup / destination address on the map.
          </Typography>
        </Box>
      )}
      {/* Driver name legend — a color swatch per driver, matching their route
          color on the map. The per-driver "Status: … In Progress / Offline"
          chip row that used to sit here was removed; that trip status and device
          presence now live in the Assigned Driver Info panel. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mx: 1, mt: 1 }}>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
          {tracks.map((t) => (
            <Box key={`legend-${t.uid}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 4, bgcolor: t.color, borderRadius: 1, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: t.color }}>
                {t.name}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Line-style key behind a hover info icon at the top-right, so it never
            reads as another driver and stays out of the way. Shown only when a
            route actually has a gap to explain. The two entries stack vertically
            in the tooltip. */}
        {hasTrackingGap && (
          <Tooltip
            arrow
            placement="bottom-end"
            title={
              <Box sx={{ py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6 }}>
                  <Box sx={{ width: 20, flexShrink: 0, borderTop: '3px solid', borderColor: 'common.white' }} />
                  <Typography variant="caption">Tracked path</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 20, flexShrink: 0, borderTop: '2px dashed', borderColor: 'common.white' }} />
                  <Typography variant="caption">No GPS data</Typography>
                </Box>
              </Box>
            }
          >
            <IconButton size="small" sx={{ flexShrink: 0, mt: -0.5, color: 'text.secondary' }} aria-label="Route line legend">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={17} onLoad={onMapLoad} onZoomChanged={handleZoomChanged}>
        {tracks.map((t, i) => {
          const offsetTrackPath = trackRenderData[i].offsetTrackPath;
          if (t.path.length <= 1 || distMeters(t.path[0], t.path[t.path.length - 1]) <= 20) {
            return null;
          }
          // One line per segment: solid runs, plus a dashed, faded edge wherever
          // reporting stopped for longer than TRACKING_GAP_MS (no data there).
          return buildTrackSegments(offsetTrackPath, t.pathTimestamps).map((seg, si) => (
            <Polyline
              key={`line-${t.uid}-${si}`}
              path={seg.points}
              options={
                seg.dashed
                  ? {
                      strokeColor: t.color,
                      strokeOpacity: 0,
                      icons: [{ icon: GAP_DASH, offset: '0', repeat: '18px' }]
                    }
                  : {
                      strokeColor: t.color,
                      strokeWeight: 5,
                      strokeOpacity: 0.9
                    }
              }
            />
          ));
        })}
        {visibleMarkers.map((m) => (
          <TrackMarker
            key={m.key}
            position={m.pos}
            color={m.color}
            label={m.name}
            badge={m.badge}
            isActive={m.isActive}
            labelOffsetPx={32 + (labelTiers.get(m.key) || 0) * 22}
          />
        ))}
        {addrPoints.map((a) => (
          <AddressMarker
            key={`addr-${a.label}`}
            position={a.pos}
            color={a.color}
            label={a.label}
          />
        ))}
      </GoogleMap>
    </Box>
  );
};

export default LiveTrackingMap;
